import { useState } from 'react';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { signIn } from 'aws-amplify/auth';
import FingerprintOutlinedIcon from '@mui/icons-material/FingerprintOutlined';
import styles from './CustomSignIn.module.css';

/**
 * Rendered as the Authenticator's SignIn.Footer (Trello Ea93pH0C). Amplify's default
 * sign-in form (email + password) handles the reliable CLIENT-BASED password flow ->
 * the machine's own TOTP screen. This adds a passkey option beneath it: a dedicated
 * button that calls signIn(USER_AUTH/WEB_AUTHN) directly, so WE catch the no-passkey
 * error and show it in place instead of the Authenticator looping. On success, Amplify's
 * Hub fires `signedIn` and the Authenticator renders the app. Also re-adds the
 * forgot-password link (overriding SignIn.Footer replaces the default one).
 */
export default function CustomSignInFooter() {
  const { toForgotPassword, isPending } = useAuthenticator((context) => [
    context.toForgotPassword,
    context.isPending,
  ]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const handlePasskey = async () => {
    setError('');
    // Reuse the email the user typed into Amplify's own sign-in field.
    const field =
      document.querySelector('input[name="username"]') ||
      document.querySelector('input[type="email"]');
    const username = (field?.value || '').trim();
    if (!username) {
      setError('Enter your email above, then choose “Sign in with a passkey”.');
      return;
    }
    setBusy(true);
    try {
      const { isSignedIn } = await signIn({
        username,
        options: { authFlowType: 'USER_AUTH', preferredChallenge: 'WEB_AUTHN' },
      });
      if (isSignedIn) {
        // signIn ran OUTSIDE the Authenticator's own machine, so it won't advance its
        // route on its own. Remount the Authenticator: on mount it reads the now-valid
        // session and renders the app directly (no sign-in screen). A user-verified
        // passkey satisfies MFA (Design B), so there is no further step here.
        window.location.reload();
        return;
      }
      // Unexpected extra step for a passkey sign-in — fall back to password.
      setError('Additional verification is needed. Please sign in with your password above.');
      setBusy(false);
    } catch (err) {
      if (err?.name !== 'NotAllowedError' && err?.name !== 'AbortError') {
        setError('No passkey found for this device, or sign-in was canceled. Sign in with your password above.');
      }
      setBusy(false);
    }
  };

  return (
    <div className={styles.footer}>
      <div className={styles.divider}>or</div>
      <button
        type="button"
        className={styles.passkeyBtn}
        onClick={handlePasskey}
        disabled={busy || isPending}
      >
        <FingerprintOutlinedIcon aria-hidden="true" fontSize="small" />
        {busy ? 'Waiting for passkey…' : 'Sign in with a passkey'}
      </button>
      {error && <div className={styles.error} role="alert">{error}</div>}
      <div className={styles.linkRow}>
        <button type="button" className={styles.linkBtn} onClick={toForgotPassword}>
          Forgot your password?
        </button>
      </div>
    </div>
  );
}
